<template>
  <div class="impulse-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$I = ∫ F dt = Δp$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 物理场景区 -->
      <div class="simulation-stage dark-stage" style="flex: 1; min-height: 150px;">
        <svg width="100%" height="100%" viewBox="0 0 400 150">
          <defs>
             <linearGradient id="bufferGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#334155" />
                <stop offset="100%" stop-color="#0f172a" />
             </linearGradient>
             <radialGradient id="glowRed" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="rgba(239, 68, 68, 0.4)" />
                <stop offset="100%" stop-color="rgba(239, 68, 68, 0)" />
             </radialGradient>
          </defs>
          
          <!-- 墙壁基础 -->
          <rect x="360" y="20" width="30" height="110" fill="url(#bufferGrad)" rx="2" />
          
          <!-- 缓冲材料层 (厚度随设置变化) -->
          <rect v-if="buffer > 5" :x="360 - buffer/2.5" y="20" :width="buffer/2.5" height="110" fill="rgba(6, 182, 212, 0.1)" :stroke="collisionActive ? '#06b6d4' : 'rgba(6, 182, 212, 0.3)'" stroke-width="2" />
          <!-- 压缩形变效果 (在碰撞中) -->
          <rect v-if="collisionActive" :x="ballX + 14" y="20" :width="Math.max(0, 360 - (ballX + 14))" height="110" fill="rgba(245, 158, 11, 0.3)" />

          <!-- 地面 -->
          <line x1="0" y1="130" x2="400" y2="130" stroke="#334155" stroke-width="2" />
          
          <!-- 运动测试球 -->
          <g :transform="`translate(${ballX}, 75)`">
            <!-- 速度拖尾 -->
            <line v-if="!collisionActive && ballX > 40" x1="-40" y1="0" x2="-14" y2="0" stroke="#38bdf8" stroke-width="4" opacity="0.3" stroke-linecap="round" />
            <line v-if="ballX < 360 && ballX > 40 && !collisionActive && ballX < 100 && frameId && ballX < 150" x1="14" y1="0" x2="40" y2="0" stroke="#38bdf8" stroke-width="4" opacity="0.3" stroke-linecap="round" /> <!-- Return trail -->

            <!-- 碰撞受力光晕 -->
            <circle v-if="collisionActive" r="24" fill="url(#glowRed)" />
            
            <circle r="14" fill="#f8fafc" stroke="#94a3b8" stroke-width="2" />
            <!-- 指示变色核心 -->
            <circle r="6" :fill="collisionActive ? '#ef4444' : '#0ea5e9'" />
          </g>
          
          <text x="15" y="25" fill="#64748b" font-size="10" font-weight="700">碰撞初速度 v₀ = 常量</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>缓冲材料特性</label>
              <span class="val">{{ bufferLabel }}</span>
           </div>
        </div>
      </div>

      <!-- F-t 图像区 -->
      <div class="graph-area-pro">
        <label class="chart-title"><i class="el-icon-s-data"></i> 碰撞接触力 F(t) 动态捕获</label>
        <svg width="100%" height="80" viewBox="0 0 400 80">
          <line x1="40" y1="70" x2="380" y2="70" stroke="#475569" marker-end="url(#arr-axis)" />
          <line x1="40" y1="70" x2="40" y2="10" stroke="#475569" marker-end="url(#arr-axis)" />
          
          <!-- 理论 F-t 曲线填充区 (冲量积分) -->
          <path :d="impulsePath" fill="rgba(239, 68, 68, 0.15)" stroke="#ef4444" stroke-width="2" />
          
          <!-- 实时扫描点 -->
          <circle v-if="collisionActive" :cx="40 + 60 + collisionTime * (200/buffer)" :cy="70 - currentForce * 0.12" r="4" fill="#fcd34d" stroke="#b45309" stroke-width="1.5" />

          <text x="375" y="80" fill="#64748b" font-size="9" font-weight="700">t</text>
          <text x="25" y="15" fill="#64748b" font-size="9" font-weight="700">F</text>
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-end gap-l">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节缓冲垫厚度 (延长作用时间 Δt)</label>
                    <el-slider v-model="buffer" :min="10" :max="100" size="small"></el-slider>
                 </div>
                 <div class="force-display" style="text-align: right; min-width: 100px;">
                    <label style="font-size:10px;color:#94a3b8;">理论最大冲击力 F_max</label>
                    <span class="val" :style="{ color: buffer < 30 ? '#ef4444' : '#1e293b', fontSize: '18px', fontWeight: '800', fontFamily: 'monospace' }">
                       {{ (3000 / buffer).toFixed(0) }} <small style="font-size:11px; color:#64748b;">N</small>
                    </span>
                 </div>
                 <div>
                    <el-button type="primary" size="small" @click="runSimulation" icon="el-icon-video-play" :disabled="runningAnimation" style="min-width:120px;font-weight:bold;">开始撞击测试</el-button>
                 </div>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>动量定理指出，物体动量的变化量等于它所受的冲量 <code>I = Δp = \int F dt</code>。在这个测试中，小轿球不管撞哪种材料，由于初速度和反弹速度设定一致，其<strong>总动量改变 <code>Δp</code> 是绝对相同的</strong>（即冲量积分，F-t 曲线下面积恒定）。但如果加厚缓冲海绵，延长了碰撞作用时间 <code>Δt</code>，那么碰撞过程中产生的峰值冲击力 <code>F_max</code> 就会成比例等效锐减！这就是汽车安全气囊、跳高海绵垫能救人一命的硬核物理原理。</p>
        </div>
      </div>
    </div>

    <defs>
      <marker id="arr-axis" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
         <path d="M0,0 L6,3 L0,6 Z" fill="#475569" />
      </marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'ImpulseMomentumViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      buffer: 40,
      ballX: 40,
      collisionActive: false,
      collisionTime: 0,
      currentForce: 0,
      runningAnimation: false,
      frameId: null,
      running: true
    }
  },
  computed: {
    bufferLabel() {
       if (this.buffer > 80) return '超长缓冲极软材料';
       if (this.buffer > 50) return '普通海绵垫 (适中)';
       if (this.buffer > 20) return '硬质橡胶 (较硬)';
       return '混凝土刚性墙面 (极危)';
    },
    impulsePath() {
      const peak = 3000 / this.buffer;
      const width = 200;
      const startX = 60;
      let d = `M ${40 + startX} 70`; // align to ground 70
      
      for(let x=0; x<=width; x++) {
          const t = x / width;
          // Force profile modelled as sine squared for impulse lobe
          const f = peak * Math.pow(Math.sin(Math.PI * t), 2);
          d += ` L ${40 + startX + x} ${70 - f * 0.12}`;
      }
      d += ` L ${40 + startX + width} 70 Z`;
      return d;
    }
  },
  methods: {
    runSimulation() {
      this.stopAnimation();
      this.ballX = 40;
      this.collisionActive = false;
      this.collisionTime = 0;
      this.runningAnimation = true;
      this.animate();
    },
    stopAnimation() {
       if (this.frameId) cancelAnimationFrame(this.frameId);
       this.frameId = null;
    },
    animate() {
       if (!this.running || !this.runningAnimation) return;
       this.stopAnimation();
       
       const vSpeed = 8;
       const targetX = 360 - 14; 

       if (!this.collisionActive) {
          this.ballX += vSpeed;
          if (this.ballX >= (targetX - this.buffer/2.5)) {
             this.ballX = targetX - this.buffer/2.5;
             this.collisionActive = true;
          }
          this.frameId = requestAnimationFrame(this.animate);
       } else {
          // Time steps scaled by buffer so impulse duration scales linearly with buffer size
          this.collisionTime += Math.max(0.2, 16 / this.buffer);
          const t = this.collisionTime / this.buffer;
          
          if (t <= 1) {
             this.currentForce = (3000 / this.buffer) * Math.pow(Math.sin(Math.PI * t), 2);
             // Physical compression visualization
             let compression = (this.currentForce / (3000 / this.buffer)) * (this.buffer/2.5 * 0.8);
             this.ballX = (targetX - this.buffer/2.5) + compression;
             this.frameId = requestAnimationFrame(this.animate);
          } else {
             this.collisionActive = false;
             this.bounceBack();
          }
       }
    },
    bounceBack() {
       this.stopAnimation();
       if (!this.running) return;
       this.ballX -= 5; // Reduced rebound velocity (inelastic)
       if (this.ballX > -20) {
          this.frameId = requestAnimationFrame(this.bounceBack);
       } else {
          this.runningAnimation = false;
          this.stopAnimation();
       }
    }
  },
  mounted() { this.running = true; },
  beforeDestroy() { 
    this.running = false;
    this.stopAnimation();
  }
}
</script>

<style scoped lang="scss">
.impulse-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 150px; background: #0f172a; border-radius: 12px; position: relative; overflow: hidden; border: 1.5px solid #1e293b; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.graph-area-pro { background: #0f172a; border-radius: 12px; padding: 12px; border: 1.5px solid #1e293b; position: relative; margin-top: -4px; .chart-title { position: absolute; top: 10px; right: 15px; font-size: 10px; color: #94a3b8; font-weight: 700; i { margin-right: 4px; color: #0ea5e9; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-end { align-items: flex-end; } .items-center { align-items: center; } .gap-l { gap: 24px; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
